// Thin fetch wrapper. The backend uses session cookies, so credentials must be
// included on every request. We surface non-2xx responses as thrown errors with
// the parsed JSON body attached for easy display in the UI.

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
  }
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  // FormData / Blob bodies need the browser to set their own content-type
  // (multipart needs a boundary). Only force JSON for the common string case.
  const isFormLike =
    typeof FormData !== "undefined" && init.body instanceof FormData;
  if (init.body && !headers.has("content-type") && !isFormLike) {
    headers.set("content-type", "application/json");
  }
  const res = await fetch(path, { ...init, headers, credentials: "include" });
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) {
    const message =
      (body && typeof body === "object" && "message" in body
        ? String((body as { message: unknown }).message)
        : null) ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, body, message);
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
